import { useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
} from "@dnd-kit/core";
import { s } from "../style.js";
import { TASK_STATUSES } from "../data.js";
import type { Task, TaskStatus } from "../types";

const repoChip = (repo: string, index?: number, names?: string[]) => (
  <span
    key={repo}
    style={s(
      "display:inline-flex; align-items:center; padding:2px 9px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-200); color:var(--color-neutral-800)"
    )}
  >
    {names?.[index ?? 0] || repo}
  </span>
);

function CardBody({ task, ghost, isRecent }: { task: Task; ghost?: boolean; isRecent?: boolean }) {
  const displayNames = task.repoNames || task.repos;
  return (
    <div
      style={s(
        "border:2px solid var(--color-divider); background:var(--color-surface); padding:16px" +
          (ghost ? "; box-shadow:0 10px 30px rgba(0,0,0,0.22)" : "")
      )}
    >
      <div
        style={s(
          "font-size:13px; letter-spacing:0.12em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600); margin-bottom:8px"
        )}
      >
        ATL-{task.id}
      </div>
      <div style={s("display:flex; align-items:center; gap:8px; margin-bottom:12px")}>
        {isRecent && (
          <span
            style={{
              ...s("width:8px; height:8px; flex:none; background:var(--color-accent); border-radius:50%"),
              animation: "alertPulse 1.8s steps(1,end) infinite",
            }}
          />
        )}
        <div
          style={s(
            "font-size:19px; line-height:1.1; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.01em; text-wrap:pretty"
          )}
        >
          {task.title}
        </div>
      </div>
      {task.repos.length > 0 && (
        <div style={s("display:flex; flex-wrap:wrap; gap:6px; margin-bottom:12px")}>
          {task.repos.slice(0, 2).map((id, i) => repoChip(id, i, displayNames))}
          {task.repos.length > 2 && (
            <span
              style={s(
                "display:inline-flex; align-items:center; padding:2px 9px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-300); color:var(--color-neutral-700)"
              )}
            >
              +{task.repos.length - 2}
            </span>
          )}
        </div>
      )}
      <div style={s("display:flex; align-items:center; gap:10px")}>
        <span
          style={s(
            "width:30px; height:30px; flex:none; background:var(--color-text); color:var(--color-bg); display:flex; align-items:center; justify-content:center; font-family:var(--font-heading); font-weight:800; font-size:12px; letter-spacing:0.02em"
          )}
        >
          {task.initials || "?"}
        </span>
        <span
          style={s(
            "font-size:15px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis"
          )}
        >
          {task.owner || "Unassigned"}
        </span>
      </div>
    </div>
  );
}

function KanbanCard({ task, onOpenTask, isRecent }: { task: Task; onOpenTask: (task: Task) => void; isRecent?: boolean }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onOpenTask(task)}
      style={{
        ...s("flex:none; cursor:pointer; touch-action:none"),
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <CardBody task={task} isRecent={isRecent} />
    </div>
  );
}

function KanbanColumn({ status, last, items, onOpenTask, recentlyUpdated }: { status: TaskStatus; last: boolean; items: Task[]; onOpenTask: (task: Task) => void; recentlyUpdated: Set<string> }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const alarm = status === "On hold";
  return (
    <div
      ref={setNodeRef}
      style={s(
        "min-width:0; min-height:0; display:flex; flex-direction:column; overflow:hidden" +
          (last ? "" : "; border-right:2px solid var(--color-divider)")
      )}
    >
      <div
        style={{
          ...s(
            "flex:none; display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:2px solid var(--color-divider)"
          ),
          background: alarm ? "var(--color-accent)" : "transparent",
        }}
      >
        <span
          style={{
            ...s(
              "font-size:13px; letter-spacing:0.12em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600)"
            ),
            color: alarm ? "var(--on-accent)" : undefined,
          }}
        >
          {status}
        </span>
        <span
          style={{
            ...s(
              "font-size:13px; letter-spacing:0.12em; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600); font-variant-numeric:tabular-nums"
            ),
            color: alarm ? "var(--on-accent)" : undefined,
          }}
        >
          {items.length}
        </span>
      </div>
      <div
        style={{
          ...s(
            "flex:1; min-height:0; overflow:auto; padding:16px; display:flex; flex-direction:column; gap:12px"
          ),
          background: isOver ? "var(--color-neutral-200)" : "transparent",
        }}
      >
        {items.map((t) => (
          <KanbanCard key={t.id} task={t} onOpenTask={onOpenTask} isRecent={recentlyUpdated.has(t.id)} />
        ))}
      </div>
    </div>
  );
}

export default function KanbanBoard({ tasks, onOpenTask, onUpdate, recentlyUpdated }: { tasks: Task[]; onOpenTask: (task: Task) => void; onUpdate: (id: string, updates: { status?: TaskStatus; repos?: string[]; ownerId?: string | null }) => void; recentlyUpdated: Set<string> }) {
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [activeWidth, setActiveWidth] = useState(240);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = (event: any) => {
    const task = tasks.find((t) => t.id === String(event.active.id));
    setActiveTask(task || null);
    const rect =
      event.active.rect.current.translated || event.active.rect.current.initial;
    if (rect) setActiveWidth(rect.width);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (over) {
      const id = String(active.id);
      const status = String(over.id);
      const task = tasks.find((t) => t.id === id);
      if (task && task.status !== status) onUpdate(id, { status: status as TaskStatus });
    }
    setActiveTask(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveTask(null)}
    >
      <div
        style={s(
          "flex:1; display:grid; grid-template-columns:repeat(5,1fr); min-height:0; overflow:hidden"
        )}
      >
        {TASK_STATUSES.map((status, i) => (
          <KanbanColumn
            key={status}
            status={status as TaskStatus}
            last={i === TASK_STATUSES.length - 1}
            items={tasks.filter((t) => t.status === status)}
            onOpenTask={onOpenTask}
            recentlyUpdated={recentlyUpdated}
          />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeTask ? (
          <div style={{ ...s("cursor:grabbing"), width: activeWidth }}>
            <CardBody task={activeTask} ghost />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
